import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { PriorityBadge } from '../components/common/PriorityBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, CalendarClock, Play } from 'lucide-react';
import { getSchedules, getVehicles, createSchedule, updateSchedule, deleteSchedule, generateWorkOrderFromSchedule } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

const CATEGORIES = [
    { value: 'mesin_transmisi', label: 'Mesin & Transmisi' },
    { value: 'sistem_rem', label: 'Sistem Rem' },
    { value: 'ban_velg', label: 'Ban & Velg' },
    { value: 'tangki_pompa', label: 'Tangki & Pompa' },
    { value: 'flowmeter', label: 'Flowmeter' },
    { value: 'kelistrikan', label: 'Kelistrikan' },
    { value: 'body_chassis', label: 'Body & Chassis' },
    { value: 'safety', label: 'Safety Equipment' },
    { value: 'dokumen', label: 'Dokumen' }
];

const TRIGGER_TYPES = [
    { value: 'km', label: 'Berbasis KM' },
    { value: 'waktu', label: 'Berbasis Waktu' },
    { value: 'keduanya', label: 'KM atau Waktu' }
];

const PRIORITIES = ['low', 'medium', 'high', 'critical'];

export default function SchedulesPage() {
    const [schedules, setSchedules] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [formData, setFormData] = useState({
        vehicle_id: '',
        judul: '',
        kategori: 'mesin_transmisi',
        deskripsi: '',
        trigger_type: 'keduanya',
        km_interval: '',
        time_interval_days: '',
        next_due_km: '',
        next_due_date: '',
        estimated_cost: '',
        priority: 'medium',
        assigned_to: '',
        is_active: true
    });

    useEffect(() => {
        fetchData();
    }, [activeFilter, categoryFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {};
            if (activeFilter === 'active') params.is_active = true;
            if (activeFilter === 'inactive') params.is_active = false;
            if (categoryFilter !== 'all') params.kategori = categoryFilter;
            
            const [schedulesRes, vehiclesRes] = await Promise.all([
                getSchedules(params),
                getVehicles()
            ]);
            setSchedules(schedulesRes.data);
            setVehicles(vehiclesRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Gagal memuat data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (schedule = null) => {
        if (schedule) {
            setEditingSchedule(schedule);
            setFormData({
                vehicle_id: schedule.vehicle_id || '',
                judul: schedule.judul || '',
                kategori: schedule.kategori || 'mesin_transmisi',
                deskripsi: schedule.deskripsi || '',
                trigger_type: schedule.trigger_type || 'keduanya',
                km_interval: schedule.km_interval || '',
                time_interval_days: schedule.time_interval_days || '',
                next_due_km: schedule.next_due_km || '',
                next_due_date: schedule.next_due_date ? schedule.next_due_date.split('T')[0] : '',
                estimated_cost: schedule.estimated_cost || '',
                priority: schedule.priority || 'medium',
                assigned_to: schedule.assigned_to || '',
                is_active: schedule.is_active !== false
            });
        } else {
            setEditingSchedule(null);
            setFormData({
                vehicle_id: '',
                judul: '',
                kategori: 'mesin_transmisi',
                deskripsi: '',
                trigger_type: 'keduanya',
                km_interval: '',
                time_interval_days: '',
                next_due_km: '',
                next_due_date: '',
                estimated_cost: '',
                priority: 'medium',
                assigned_to: '',
                is_active: true
            });
        }
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                km_interval: formData.km_interval ? parseFloat(formData.km_interval) : null,
                time_interval_days: formData.time_interval_days ? parseInt(formData.time_interval_days) : null,
                next_due_km: formData.next_due_km ? parseFloat(formData.next_due_km) : null,
                next_due_date: formData.next_due_date ? new Date(formData.next_due_date).toISOString() : null,
                estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null
            };

            if (editingSchedule) {
                await updateSchedule(editingSchedule.id, submitData);
                toast.success('Jadwal berhasil diupdate');
            } else {
                await createSchedule(submitData);
                toast.success('Jadwal berhasil dibuat');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to save schedule:', error);
            toast.error('Gagal menyimpan jadwal');
        }
    };

    const handleDelete = async (schedule) => {
        if (window.confirm(`Yakin ingin menghapus "${schedule.judul}"?`)) {
            try {
                await deleteSchedule(schedule.id);
                toast.success('Jadwal berhasil dihapus');
                fetchData();
            } catch (error) {
                console.error('Failed to delete schedule:', error);
                toast.error('Gagal menghapus jadwal');
            }
        }
    };

    const handleGenerateWorkOrder = async (schedule) => {
        try {
            await generateWorkOrderFromSchedule(schedule.id);
            toast.success('Work Order berhasil dibuat');
        } catch (error) {
            console.error('Failed to generate work order:', error);
            toast.error('Gagal membuat Work Order');
        }
    };

    const filteredSchedules = schedules.filter(schedule =>
        schedule.judul.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (schedule.vehicle_nomor_polisi && schedule.vehicle_nomor_polisi.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const columns = [
        {
            header: 'Kendaraan',
            accessor: 'vehicle_nomor_polisi',
            render: (value) => (
                <span className="font-mono text-xs uppercase tracking-wider text-primary font-medium">
                    {value}
                </span>
            )
        },
        {
            header: 'Jadwal Service',
            accessor: 'judul',
            render: (value, row) => (
                <div>
                    <p className="font-medium">{value}</p>
                    <p className="text-xs text-muted-foreground capitalize">{row.kategori?.replace('_', ' ')}</p>
                </div>
            )
        },
        {
            header: 'Trigger',
            accessor: 'trigger_type',
            render: (value, row) => (
                <div className="text-sm">
                    {value === 'km' && <span>Setiap {row.km_interval?.toLocaleString()} km</span>}
                    {value === 'waktu' && <span>Setiap {row.time_interval_days} hari</span>}
                    {value === 'keduanya' && (
                        <div>
                            <span>{row.km_interval?.toLocaleString()} km</span>
                            <span className="text-muted-foreground"> / {row.time_interval_days} hari</span>
                        </div>
                    )}
                </div>
            )
        },
        {
            header: 'Due Berikutnya',
            accessor: 'next_due_date',
            render: (value, row) => (
                <div className="text-sm">
                    {row.next_due_km && <p className="font-mono">{row.next_due_km?.toLocaleString()} km</p>}
                    {value && <p className="text-muted-foreground">{formatDate(value)}</p>}
                </div>
            )
        },
        {
            header: 'Est. Biaya',
            accessor: 'estimated_cost',
            render: (value) => value ? <span className="text-sm">Rp {value?.toLocaleString()}</span> : '-'
        },
        {
            header: 'Priority',
            accessor: 'priority',
            render: (value) => <PriorityBadge priority={value} />
        },
        {
            header: 'Status',
            accessor: 'is_active',
            render: (value) => (
                <span className={`status-badge ${value ? 'status-completed' : 'status-pending'}`}>
                    {value ? 'Aktif' : 'Nonaktif'}
                </span>
            )
        },
        {
            header: '',
            accessor: 'actions',
            render: (_, row) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`schedule-actions-${row.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleGenerateWorkOrder(row)}>
                            <Play className="h-4 w-4 mr-2" />
                            Buat Work Order
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleOpenDialog(row)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            onClick={() => handleDelete(row)}
                            className="text-destructive focus:text-destructive"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Hapus
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    ];

    return (
        <Layout title="Jadwal Service">
            <div className="space-y-6" data-testid="schedules-page">
                {/* Header Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="flex flex-1 gap-3 w-full sm:w-auto flex-wrap">
                        <div className="relative flex-1 sm:w-64 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Cari jadwal..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 rounded-sm"
                                data-testid="search-schedules"
                            />
                        </div>
                        <Select value={activeFilter} onValueChange={setActiveFilter}>
                            <SelectTrigger className="w-[120px] rounded-sm" data-testid="active-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua</SelectItem>
                                <SelectItem value="active">Aktif</SelectItem>
                                <SelectItem value="inactive">Nonaktif</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger className="w-[160px] rounded-sm" data-testid="category-filter">
                                <SelectValue placeholder="Kategori" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Kategori</SelectItem>
                                {CATEGORIES.map(c => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button 
                        onClick={() => handleOpenDialog()} 
                        className="industrial-btn-primary"
                        data-testid="add-schedule-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Buat Jadwal
                    </Button>
                </div>

                {/* Schedules Table */}
                <DataTable
                    columns={columns}
                    data={filteredSchedules}
                    loading={loading}
                    emptyMessage="Tidak ada jadwal"
                    emptyIcon={CalendarClock}
                />

                {/* Add/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="section-title">
                                {editingSchedule ? 'Edit Jadwal' : 'Buat Jadwal Baru'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingSchedule ? 'Update jadwal maintenance.' : 'Buat jadwal preventive maintenance baru.'}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-2 gap-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="vehicle_id">Kendaraan *</Label>
                                    <Select 
                                        value={formData.vehicle_id} 
                                        onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                                    >
                                        <SelectTrigger className="rounded-sm" data-testid="schedule-vehicle-select">
                                            <SelectValue placeholder="Pilih kendaraan" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {vehicles.map(v => (
                                                <SelectItem key={v.id} value={v.id}>{v.nomor_polisi} - {v.merk}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="judul">Judul *</Label>
                                    <Input
                                        id="judul"
                                        value={formData.judul}
                                        onChange={(e) => setFormData({ ...formData, judul: e.target.value })}
                                        required
                                        className="rounded-sm"
                                        placeholder="Ganti Oli Mesin"
                                        data-testid="schedule-judul-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="kategori">Kategori *</Label>
                                    <Select 
                                        value={formData.kategori} 
                                        onValueChange={(value) => setFormData({ ...formData, kategori: value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map(c => (
                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="trigger_type">Trigger *</Label>
                                    <Select 
                                        value={formData.trigger_type} 
                                        onValueChange={(value) => setFormData({ ...formData, trigger_type: value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TRIGGER_TYPES.map(t => (
                                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                {(formData.trigger_type === 'km' || formData.trigger_type === 'keduanya') && (
                                    <>
                                        <div className="space-y-2">
                                            <Label htmlFor="km_interval">Interval KM</Label>
                                            <Input
                                                id="km_interval"
                                                type="number"
                                                value={formData.km_interval}
                                                onChange={(e) => setFormData({ ...formData, km_interval: e.target.value })}
                                                className="rounded-sm"
                                                placeholder="10000"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="next_due_km">Due Berikutnya (km)</Label>
                                            <Input
                                                id="next_due_km"
                                                type="number"
                                                value={formData.next_due_km}
                                                onChange={(e) => setFormData({ ...formData, next_due_km: e.target.value })}
                                                className="rounded-sm"
                                            />
                                        </div>
                                    </>
                                )}
                                
                                {(formData.trigger_type === 'waktu' || formData.trigger_type === 'keduanya') && (
                                    <>
                                        <div className="space-y-2">
                                            <Label htmlFor="time_interval_days">Interval (Hari)</Label>
                                            <Input
                                                id="time_interval_days"
                                                type="number"
                                                value={formData.time_interval_days}
                                                onChange={(e) => setFormData({ ...formData, time_interval_days: e.target.value })}
                                                className="rounded-sm"
                                                placeholder="90"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="next_due_date">Due Berikutnya (Tanggal)</Label>
                                            <Input
                                                id="next_due_date"
                                                type="date"
                                                value={formData.next_due_date}
                                                onChange={(e) => setFormData({ ...formData, next_due_date: e.target.value })}
                                                className="rounded-sm"
                                            />
                                        </div>
                                    </>
                                )}
                                
                                <div className="space-y-2">
                                    <Label htmlFor="priority">Prioritas</Label>
                                    <Select 
                                        value={formData.priority} 
                                        onValueChange={(value) => setFormData({ ...formData, priority: value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PRIORITIES.map(p => (
                                                <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="estimated_cost">Est. Biaya (Rp)</Label>
                                    <Input
                                        id="estimated_cost"
                                        type="number"
                                        value={formData.estimated_cost}
                                        onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="assigned_to">Ditugaskan Ke</Label>
                                    <Input
                                        id="assigned_to"
                                        value={formData.assigned_to}
                                        onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                                        className="rounded-sm"
                                        placeholder="Bengkel A"
                                    />
                                </div>
                                <div className="flex items-center space-x-2 pt-6">
                                    <Switch
                                        id="is_active"
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                    />
                                    <Label htmlFor="is_active">Aktif</Label>
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="deskripsi">Deskripsi</Label>
                                    <Textarea
                                        id="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={(e) => setFormData({ ...formData, deskripsi: e.target.value })}
                                        className="rounded-sm"
                                        rows={2}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                                    Batal
                                </Button>
                                <Button type="submit" className="industrial-btn-primary" data-testid="save-schedule-btn">
                                    {editingSchedule ? 'Update' : 'Simpan'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
